import { Box, Typography } from "@mui/material";
import { PieChart } from "@mui/x-charts/PieChart";
import { useMemo } from "react";
import useAPI from "../../../../api/hooks/useAPI";
import useColors from "../../../../api/hooks/useColors";
import { ControlSeverityMssp } from "../../../../utils/constants";
import IntegrationImage from "../../../stack/integrations/components/IntegrationImage";
import { getSeverityColor } from "./utils";

const PieChartTool = ({ data, setSelectedTool, showNotDeployed }) => {
  const colors = useColors();
  const { trackEvent } = useAPI();

  const notDeployedValue = data.not_deployed_tenants?.length;

  const chartData = useMemo(() => {
    const mappedData = Object.entries(data.controls).map(([key, value]) => ({
      id: key,
      value:
        key?.toLowerCase() === "not deployed"
          ? value?.length * notDeployedValue
          : value?.length,
      label:
        key.charAt(0).toUpperCase() +
        key.slice(1).toLowerCase() +
        (key !== ControlSeverityMssp.NOT_DEPLOYED &&
        key !== ControlSeverityMssp.COMPLIANT
          ? " Severity Gaps"
          : ""),
      color: getSeverityColor(key, colors),
    }));

    const filteredData = mappedData.filter((data) => {
      const shouldFilter = data.id !== ControlSeverityMssp.NOT_DEPLOYED;

      if (!showNotDeployed) {
        return shouldFilter;
      }

      return true;
    });

    return filteredData;
  }, [data.controls, showNotDeployed, notDeployedValue]);

  const totalValue = chartData.reduce((acc, item) => acc + item.value, 0);
  const allNotDeployed =
    data.tenants.length === 0 && data.not_deployed_tenants.length > 0;

  return (
    <Box
      sx={{
        height: "100%",
        minWidth: "200px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 3,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "center",
          gap: 1,
          border: "1px solid " + colors.icon_stroke,
          padding: "6px",
          width: "48px",
          height: "48px",
          borderRadius: "8px",
        }}
      >
        <IntegrationImage
          name={data.id}
          width={36}
          showToolTip={true}
          newLogo={true}
        />
      </Box>
      <Box
        sx={{
          position: "relative",
          width: "100%",
          height: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          cursor: "pointer",
        }}
        onClick={() => {
          setSelectedTool(data);
          trackEvent("User clicked on Pie Chart and opened the Drawer");
        }}
      >
        <PieChart
          series={[
            {
              data: chartData,
              innerRadius: 60,
              outerRadius: 70,
              paddingAngle: 1,
              startAngle: -90,
              endAngle: 270,
              cornerRadius: 3,
              highlightScope: {
                fade: "global",
                highlight: "none",
              },
              faded: { color: colors.gray_soft },

              id: "pie-segment",
            },
          ]}
          slotProps={{
            legend: {
              hidden: true,
            },
          }}
          height={145}
          width={145}
          margin={{ top: 0, bottom: 0, left: 0, right: 0 }}
        />
        <Box
          sx={{
            position: "absolute",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)",
            textAlign: "center",
            width: "100%",
            maxWidth: 120,
          }}
        >
          <Typography variant="h6" component="div">
            {totalValue}
          </Typography>
          <Typography variant="body2">
            {!allNotDeployed ? "Controls" : "Controls unmonitored"}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default PieChartTool;
